import { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Facebook,
  Instagram,
  Home,
  ShoppingBag,
  Phone,
  Mail,
  ArrowRight,
  ArrowLeft,
  ExternalLink,
} from "lucide-react";
import AnimatedLogo from "../components/AnimatedLogo";
import { useLanguage } from "../context/LanguageContext";
import "./InfoCard.css";

// SVG for official WhatsApp branding
function WhatsAppIcon({ size = 20, className = "" }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
    </svg>
  );
}

export default function InfoCard() {
  const { isRTL, language } = useLanguage();

  useEffect(() => {
    document.title = isRTL
      ? "بطاقة التعريف الرقمية | تيري لاند للمنسوجات الفاخرة"
      : "Digital Info Card | Terry Land Luxury Textiles";
  }, [isRTL, language]);

  const whatsappUrl = "https://wa.me/201099921862";
  const facebookUrl = "https://www.facebook.com/people/TERRY-LAND/61588050711260/";
  const instagramUrl = "https://www.instagram.com/terryland.eg/";

  return (
    <div className="info-card-page">
      <div className="info-card">
        {/* Animated Brand Emblem & Hand-Drawn Wordmark */}
        <div className="info-card__logo-wrap">
          <AnimatedLogo size={160} showWordmark={true} className="info-card__animated-logo" />
        </div>

        {/* Accessible screen-reader heading */}
        <h1 className="sr-only">TERRY LAND</h1>

        {/* Tagline */}
        <p className="info-card__tagline">
          {isRTL
            ? "أرقى أرواب الحمام والمناشف الفندقية بخامات القطن الطبيعي والمايكروفايبر والوجهين المزدوجة."
            : "Luxury bathrobes and premier linens crafted with double-sided cotton, microfiber, and innovative dual-face weaves."}
        </p>

        {/* ── Primary Action: Continue to Home Page ── */}
        <Link to="/" className="info-card__home-btn">
          <Home size={18} />
          <span>
            {isRTL
              ? "الانتقال إلى المتجر الرئيسي"
              : "Continue to Home Page"}
          </span>
          {isRTL ? <ArrowLeft size={16} /> : <ArrowRight size={16} />}
        </Link>

        {/* ── Social Media & Contact Icons ── */}
        <div className="info-card__divider">
          <span>{isRTL ? "تواصل معنا مباشرة" : "Connect With Us"}</span>
        </div>

        <div className="info-card__social-row">
          {/* WhatsApp Contacting Icon */}
          <a
            href={whatsappUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="info-card__social-icon-btn info-card__social-icon-btn--whatsapp"
            aria-label="WhatsApp"
            title={isRTL ? "محادثة واتساب مباشرة" : "Chat on WhatsApp"}
          >
            <WhatsAppIcon size={22} />
          </a>

          {/* Facebook Icon */}
          <a
            href={facebookUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="info-card__social-icon-btn info-card__social-icon-btn--facebook"
            aria-label="Facebook"
            title={isRTL ? "صفحة فيسبوك الرسمية" : "Visit Our Facebook Page"}
          >
            <Facebook size={22} />
          </a>

          {/* Instagram Icon */}
          <a
            href={instagramUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="info-card__social-icon-btn info-card__social-icon-btn--instagram"
            aria-label="Instagram"
            title={isRTL ? "حساب إنستغرام" : "Follow on Instagram"}
          >
            <Instagram size={22} />
          </a>

          {/* Quick Home Icon */}
          <Link
            to="/"
            className="info-card__social-icon-btn info-card__social-icon-btn--home"
            aria-label="Home"
            title={isRTL ? "الصفحة الرئيسية" : "Go to Home Page"}
          >
            <ShoppingBag size={22} />
          </Link>
        </div>

        {/* ── Action Cards Stack ── */}
        <div className="info-card__actions">
          {/* Direct WhatsApp Action Link */}
          <a
            href={whatsappUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="info-card__action-link"
          >
            <div className="info-card__action-left">
              <div className="info-card__action-icon">
                <WhatsAppIcon size={18} />
              </div>
              <div className="info-card__action-text">
                <span className="info-card__action-title">
                  {isRTL ? "خدمة العملاء عبر واتساب" : "WhatsApp Concierge"}
                </span>
                <span className="info-card__action-sub" dir="ltr">
                  +20 10 9992 1862
                </span>
              </div>
            </div>
            <ExternalLink size={16} className="info-card__action-arrow" />
          </a>

          {/* Phone Call Link */}
          <a href="tel:+201099921862" className="info-card__action-link">
            <div className="info-card__action-left">
              <div className="info-card__action-icon">
                <Phone size={18} />
              </div>
              <div className="info-card__action-text">
                <span className="info-card__action-title">
                  {isRTL ? "اتصال هاتفي مباشر" : "Direct Telephone"}
                </span>
                <span className="info-card__action-sub" dir="ltr">
                  +20 10 9992 1862
                </span>
              </div>
            </div>
            <ExternalLink size={16} className="info-card__action-arrow" />
          </a>

          {/* Email Link */}
          <a href="mailto:info@terryland.net" className="info-card__action-link">
            <div className="info-card__action-left">
              <div className="info-card__action-icon">
                <Mail size={18} />
              </div>
              <div className="info-card__action-text">
                <span className="info-card__action-title">
                  {isRTL ? "البريد الإلكتروني الرسمي" : "Official Inquiries"}
                </span>
                <span className="info-card__action-sub">
                  info@terryland.net
                </span>
              </div>
            </div>
            <ExternalLink size={16} className="info-card__action-arrow" />
          </a>
        </div>

        {/* Card Footer */}
        <div className="info-card__footer">
          <span className="info-card__footer-brand">TERRY LAND</span>
          <span>
            {isRTL ? "القاهرة، مصر • منذ 1956" : "Cairo, Egypt • Est. 1956"}
          </span>
        </div>
      </div>
    </div>
  );
}
